import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import {
  COST_REPORT_GROUPING_LABELS,
  formatCents,
  type CashFlowResponse,
  type CostReportGrouping,
  type CostReportResponse,
  type DreBlock,
  type DreResponse,
  type DreSection,
  type DueScheduleResponse,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useBusinesses, useCostCenters, monthRange } from '@/lib/queries';
import { PeriodBar } from '@/components/PeriodBar';
import { downloadCsv } from '@/lib/csv';

/* ========================= Agenda de vencimentos ========================== */

export function AgendaPage() {
  const range = monthRange();
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [businessId, setBusinessId] = useState('');

  const query = useQuery({
    queryKey: ['due-schedule', from, to, businessId],
    queryFn: () =>
      api.get<DueScheduleResponse>('/financial/due-schedule', {
        from,
        to,
        type: 'PAYABLE',
        businessId: businessId || undefined,
      }),
  });

  return (
    <>
      <WorkspaceHeader
        title="Agenda de vencimentos"
      />

      <div className="workspace__body">
        <PeriodBar from={from} to={to} onFrom={setFrom} onTo={setTo}>
          <EmpresaFiltro value={businessId} onChange={setBusinessId} />
        </PeriodBar>

        {query.isPending && <LoadingState />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {query.data && (
          <>
            <div className="metrics" style={{ marginBottom: 18 }}>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Total a vencer</span>
                </div>
                <div className="metric__value">{formatCents(query.data.totalCents)}</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Já vencido</span>
                </div>
                <div
                  className={`metric__value${query.data.overdueCents > 0 ? ' metric__value--bad' : ''}`}
                >
                  {formatCents(query.data.overdueCents)}
                </div>
              </div>
            </div>

            {query.data.days.length === 0 ? (
              <EmptyState
                title="Nada vence neste período"
                description="Escolha outro intervalo para ver os próximos vencimentos."
              />
            ) : (
              query.data.days.map((day) => (
                <section key={day.date} style={{ marginBottom: 18 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 8,
                    }}
                  >
                    <h2 className="mono" style={{ fontSize: 14, margin: 0 }}>
                      {formatDate(day.date)}
                    </h2>
                    {day.overdue && <StatusBadge kind="bad">Vencido</StatusBadge>}
                    <span className="muted" style={{ fontSize: 12.5 }}>
                      {day.count} conta(s)
                    </span>
                    <div style={{ flex: 1 }} />
                    <span className="mono strong">{formatCents(day.totalCents)}</span>
                  </div>

                  <div className="table-wrap">
                    <table className="table">
                      <caption className="sr-only">Contas que vencem em {day.date}</caption>
                      <tbody>
                        {day.items.map((item) => (
                          <tr key={item.installmentId}>
                            <td className="mono is-strong" style={{ width: 130 }}>
                              {item.code}
                            </td>
                            <td>{item.description}</td>
                            <td>{item.personName ?? '—'}</td>
                            <td className="muted">{item.costCenterName ?? '—'}</td>
                            <td className="mono" style={{ width: 90 }}>
                              {item.number}/{item.totalInstallments}
                            </td>
                            <td className="is-right mono is-strong" style={{ width: 130 }}>
                              {formatCents(item.openCents)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ============================= Fluxo de caixa ============================= */

export function FluxoCaixaPage() {
  const range = monthRange();
  const [from, setFrom] = useState('2026-01-01');
  const [to, setTo] = useState(range.to);
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('month');
  const [costCenterId, setCostCenterId] = useState('');
  const [businessId, setBusinessId] = useState('');
  const costCenters = useCostCenters(false);

  const query = useQuery({
    queryKey: ['cash-flow', from, to, granularity, costCenterId, businessId],
    queryFn: () =>
      api.get<CashFlowResponse>('/financial/cash-flow', {
        from,
        to,
        granularity,
        costCenterId: costCenterId || undefined,
        businessId: businessId || undefined,
      }),
  });

  const columns: Array<Column<CashFlowResponse['buckets'][number] & { id: string }>> = [
    { key: 'period', label: 'Período', mono: true, strong: true, render: (row) => row.period },
    {
      key: 'realizedIn',
      label: 'Entradas realizadas',
      right: true,
      mono: true,
      render: (row) => formatCents(row.realizedInCents),
    },
    {
      key: 'realizedOut',
      label: 'Saídas realizadas',
      right: true,
      mono: true,
      render: (row) => formatCents(row.realizedOutCents),
    },
    {
      key: 'projectedOut',
      label: 'Saídas previstas',
      right: true,
      mono: true,
      render: (row) => (
        <span style={{ color: T.amber }}>{formatCents(row.projectedOutCents)}</span>
      ),
    },
    {
      key: 'net',
      label: 'Resultado',
      right: true,
      mono: true,
      render: (row) => (
        <span style={{ color: row.netCents < 0 ? T.red : T.green, fontWeight: 600 }}>
          {formatCents(row.netCents)}
        </span>
      ),
    },
    {
      key: 'accumulated',
      label: 'Acumulado',
      right: true,
      mono: true,
      render: (row) => formatCents(row.accumulatedCents),
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Fluxo de caixa"
      />

      <div className="workspace__body">
        <PeriodBar from={from} to={to} onFrom={setFrom} onTo={setTo}>
          <EmpresaFiltro value={businessId} onChange={setBusinessId} />
          <select
            className="filterbar__select"
            value={granularity}
            aria-label="Agrupamento"
            onChange={(event) => setGranularity(event.target.value as 'day' | 'week' | 'month')}
          >
            <option value="day">Por dia</option>
            <option value="week">Por semana</option>
            <option value="month">Por mês</option>
          </select>
          <select
            className="filterbar__select"
            value={costCenterId}
            aria-label="Centro de custo"
            onChange={(event) => setCostCenterId(event.target.value)}
          >
            <option value="">Todos os centros de custo</option>
            {(costCenters.data ?? []).map((center) => (
              <option key={center.id} value={center.id}>
                {center.code} · {center.name}
              </option>
            ))}
          </select>
        </PeriodBar>

        {query.isPending && <LoadingState />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {query.data && (
          <>
            <div className="metrics" style={{ marginBottom: 18 }}>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Saídas realizadas</span>
                </div>
                <div className="metric__value">
                  {formatCents(query.data.totals.realizedOutCents)}
                </div>
                <div className="metric__note">já saiu do caixa</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Saídas previstas</span>
                </div>
                <div className="metric__value metric__value--warn">
                  {formatCents(query.data.totals.projectedOutCents)}
                </div>
                <div className="metric__note">parcelas em aberto no período</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Entradas realizadas</span>
                </div>
                <div className="metric__value metric__value--good">
                  {formatCents(query.data.totals.realizedInCents)}
                </div>
              </div>
            </div>

            <SmartDataTable
              caption="Fluxo de caixa por período"
              columns={columns}
              rows={query.data.buckets.map((bucket) => ({ ...bucket, id: bucket.period }))}
              emptyState={
                <EmptyState
                  title="Sem movimento neste período"
                  description="Nenhum pagamento realizado nem parcela a vencer no intervalo escolhido."
                />
              }
            />
          </>
        )}
      </div>
    </>
  );
}

/* ========================== Custo por centro de custo ===================== */

export function CustoPorCentroPage() {
  const [from, setFrom] = useState('2026-01-01');
  const [to, setTo] = useState(monthRange().to);
  const [groupBy, setGroupBy] = useState<CostReportGrouping>('costCenter');
  const [costCenterId, setCostCenterId] = useState('');
  const [businessId, setBusinessId] = useState('');
  const costCenters = useCostCenters(false);

  const query = useQuery({
    queryKey: ['cost-report', from, to, groupBy, costCenterId, businessId],
    queryFn: () =>
      api.get<CostReportResponse>('/financial/cost-report', {
        from,
        to,
        groupBy,
        costCenterId: costCenterId || undefined,
        businessId: businessId || undefined,
      }),
  });

  const columns: Array<Column<CostReportResponse['rows'][number] & { id: string }>> = [
    { key: 'code', label: 'Código', mono: true, render: (row) => row.code ?? '—' },
    { key: 'label', label: COST_REPORT_GROUPING_LABELS[groupBy], strong: true, render: (row) => row.label },
    {
      key: 'entryCount',
      label: 'Lançamentos',
      right: true,
      mono: true,
      render: (row) => String(row.entryCount),
    },
    {
      key: 'committed',
      label: 'Comprometido',
      right: true,
      mono: true,
      render: (row) => formatCents(row.committedCents),
    },
    {
      key: 'paid',
      label: 'Pago',
      right: true,
      mono: true,
      render: (row) => formatCents(row.paidCents),
    },
    {
      key: 'open',
      label: 'A pagar',
      right: true,
      mono: true,
      render: (row) => (
        <span style={{ color: row.openCents > 0 ? T.amber : undefined }}>
          {formatCents(row.openCents)}
        </span>
      ),
    },
    {
      key: 'share',
      label: '% da obra',
      right: true,
      mono: true,
      render: (row) => `${row.sharePercent.toLocaleString('pt-BR')}%`,
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Custo por centro de custo"
        actions={
          query.data && query.data.rows.length > 0 ? (
            <Button
              variant="outline"
              icon={Download}
              onClick={() =>
                downloadCsv(`custo-por-centro-${from}-a-${to}`, [
                  ['Código', 'Descrição', 'Lançamentos', 'Comprometido', 'Pago', 'A pagar', '% da obra'],
                  ...query.data!.rows.map((row) => [
                    row.code ?? '',
                    row.label,
                    String(row.entryCount),
                    (row.committedCents / 100).toFixed(2).replace('.', ','),
                    (row.paidCents / 100).toFixed(2).replace('.', ','),
                    (row.openCents / 100).toFixed(2).replace('.', ','),
                    String(row.sharePercent).replace('.', ','),
                  ]),
                ])
              }
            >
              Exportar CSV
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <PeriodBar from={from} to={to} onFrom={setFrom} onTo={setTo}>
          <EmpresaFiltro value={businessId} onChange={setBusinessId} />
          <select
            className="filterbar__select"
            value={groupBy}
            aria-label="Agrupar por"
            onChange={(event) => setGroupBy(event.target.value as CostReportGrouping)}
          >
            {Object.entries(COST_REPORT_GROUPING_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            className="filterbar__select"
            value={costCenterId}
            aria-label="Centro de custo"
            onChange={(event) => setCostCenterId(event.target.value)}
          >
            <option value="">Todos os centros de custo</option>
            {(costCenters.data ?? []).map((center) => (
              <option key={center.id} value={center.id}>
                {center.code} · {center.name}
              </option>
            ))}
          </select>
        </PeriodBar>

        {query.isPending && <LoadingState />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {query.data && (
          <>
            <div className="metrics" style={{ marginBottom: 18 }}>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Custo comprometido</span>
                </div>
                <div className="metric__value">
                  {formatCents(query.data.totals.committedCents)}
                </div>
                <div className="metric__note">valor total dos lançamentos</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Já pago</span>
                </div>
                <div className="metric__value metric__value--good">
                  {formatCents(query.data.totals.paidCents)}
                </div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Ainda a pagar</span>
                </div>
                <div className="metric__value metric__value--warn">
                  {formatCents(query.data.totals.openCents)}
                </div>
              </div>
            </div>

            <SmartDataTable
              caption="Custo por centro de custo"
              columns={columns}
              rows={query.data.rows.map((row) => ({ ...row, id: row.key }))}
              emptyState={
                <EmptyState
                  title="Sem custo lançado neste período"
                  description="Os lançamentos com competência no intervalo aparecem aqui, agrupados pelo centro de custo."
                />
              }
            />
          </>
        )}
      </div>
    </>
  );
}

/* ============================== DRE ====================================== */

/** Uma linha calculada da demonstração — não vem de conta nenhuma. */
function ResultRow({
  label,
  cents,
  note,
  total,
  percent,
}: {
  label: string;
  cents: number;
  note?: string;
  total?: boolean;
  percent?: number | null;
}) {
  return (
    <div className={`dre__row dre__row--${total ? 'total' : 'result'}`}>
      <span className="dre__code" />
      <span className="dre__label">
        {label}
        {note && (
          <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.7 }}> · {note}</span>
        )}
      </span>
      <span
        className={`dre__value${cents < 0 ? ' dre__value--bad' : ' dre__value--good'}`}
      >
        {formatCents(cents)}
      </span>
      <span className="dre__share">
        {percent === null || percent === undefined ? '' : `${percent.toLocaleString('pt-BR')}%`}
      </span>
    </div>
  );
}

/** Bloco somado a partir das contas, aberto no clique. */
function BlockRow({
  block,
  label,
  open,
  onToggle,
  overRevenueCents,
}: {
  block: DreBlock | undefined;
  label: string;
  open: boolean;
  onToggle: () => void;
  /** Receita líquida do período — o bloco aparece como % dela. */
  overRevenueCents?: number;
}) {
  if (!block || block.accounts.length === 0) return null;
  return (
    <>
      <div
        className="dre__row dre__row--block"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <span className="dre__code">
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
        </span>
        <span className="dre__label">
          {label}
          <span style={{ color: T.inkFaint, fontSize: 12 }}>
            {' '}
            · {block.accounts.length} conta(s)
          </span>
        </span>
        <span className="dre__value">{formatCents(block.totalCents)}</span>
        <span className="dre__share">
          {overRevenueCents
            ? `${(Math.round((block.totalCents / overRevenueCents) * 1000) / 10).toLocaleString('pt-BR')}%`
            : ''}
        </span>
      </div>
      {open &&
        block.accounts.map((account) => (
          <div key={account.chartAccountId} className="dre__row dre__row--account">
            <span className="dre__code">{account.code}</span>
            <span className="dre__label">{account.name}</span>
            <span className="dre__value" style={{ fontWeight: 500 }}>
              {formatCents(account.amountCents)}
            </span>
            <span className="dre__share">{account.sharePercent.toLocaleString('pt-BR')}%</span>
          </div>
        ))}
    </>
  );
}

/**
 * DRE — o "quanto sobrou" do período.
 *
 * Lucro bruto, operacional e líquido são CALCULADOS: não existem como conta no
 * plano de propósito, senão alguém lançaria despesa dentro de "Lucro líquido".
 * Investimento aparece separado, embaixo do resultado — comprar brinquedo é
 * trocar caixa por patrimônio, não é despesa do mês.
 */
export function DrePage() {
  const range = monthRange();
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [basis, setBasis] = useState<'competencia' | 'caixa'>('competencia');
  const [costCenterId, setCostCenterId] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [openBlocks, setOpenBlocks] = useState<string[]>([]);
  const costCenters = useCostCenters(false);

  const query = useQuery({
    queryKey: ['dre', from, to, basis, costCenterId, businessId],
    queryFn: () =>
      api.get<DreResponse>('/financial/dre', {
        from,
        to,
        basis,
        costCenterId: costCenterId || undefined,
        businessId: businessId || undefined,
      }),
  });

  const data = query.data;
  const block = (section: DreSection) => data?.blocks.find((b) => b.section === section);

  const toggle = (section: string) =>
    setOpenBlocks((current) =>
      current.includes(section) ? current.filter((s) => s !== section) : [...current, section],
    );

  return (
    <>
      <WorkspaceHeader
        title="DRE — resultado do período"
        actions={
          data ? (
            <Button
              variant="outline"
              icon={Download}
              onClick={() =>
                downloadCsv(`dre-${basis}-${from}-a-${to}`, [
                  ['Linha', 'Valor (R$)'],
                  ...[
                    ['Receita bruta', data.result.receitaBrutaCents],
                    ['(−) Deduções de receita', -data.result.deducoesCents],
                    ['= Receita líquida', data.result.receitaLiquidaCents],
                    ['(−) Custos operacionais diretos', -data.result.custosDiretosCents],
                    ['= Lucro bruto', data.result.lucroBrutoCents],
                    ['(−) Despesas operacionais', -data.result.despesasOperacionaisCents],
                    ['= Lucro operacional', data.result.lucroOperacionalCents],
                    ['(−) Impostos e obrigações', -data.result.impostosCents],
                    ['(±) Resultado financeiro', data.result.resultadoFinanceiroCents],
                    ['= Lucro líquido', data.result.lucroLiquidoCents],
                    ['(−) Investimentos (CAPEX)', -data.result.investimentosCents],
                    ['= Geração de caixa', data.result.geracaoCaixaCents],
                  ].map(([label, cents]) => [
                    String(label),
                    ((cents as number) / 100).toFixed(2).replace('.', ','),
                  ]),
                ])
              }
            >
              Exportar CSV
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <PeriodBar from={from} to={to} onFrom={setFrom} onTo={setTo}>
          <EmpresaFiltro value={businessId} onChange={setBusinessId} />
          <select
            className="filterbar__select"
            value={basis}
            aria-label="Regime"
            onChange={(event) => setBasis(event.target.value as 'competencia' | 'caixa')}
          >
            <option value="competencia">Competência — o mês a que o valor pertence</option>
            <option value="caixa">Caixa — o que entrou e saiu de fato</option>
          </select>
          <select
            className="filterbar__select"
            value={costCenterId}
            aria-label="Centro de custo"
            onChange={(event) => setCostCenterId(event.target.value)}
          >
            <option value="">Parque inteiro</option>
            {(costCenters.data ?? []).map((center) => (
              <option key={center.id} value={center.id}>
                {center.code} · {center.name}
              </option>
            ))}
          </select>
        </PeriodBar>

        {query.isPending && <LoadingState />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {data && (
          <>
            <div className="metrics" style={{ marginBottom: 18 }}>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Receita líquida</span>
                </div>
                <div className="metric__value">{formatCents(data.result.receitaLiquidaCents)}</div>
                <div className="metric__note">bruta menos as deduções</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Lucro operacional</span>
                </div>
                <div
                  className={`metric__value metric__value--${
                    data.result.lucroOperacionalCents >= 0 ? 'good' : 'bad'
                  }`}
                >
                  {formatCents(data.result.lucroOperacionalCents)}
                </div>
                <div className="metric__note">
                  {data.margins.operacionalPercent === null
                    ? 'sem receita no período'
                    : `${data.margins.operacionalPercent.toLocaleString('pt-BR')}% da receita líquida`}
                </div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Lucro líquido</span>
                </div>
                <div
                  className={`metric__value metric__value--${
                    data.result.lucroLiquidoCents >= 0 ? 'good' : 'bad'
                  }`}
                >
                  {formatCents(data.result.lucroLiquidoCents)}
                </div>
                <div className="metric__note">depois de imposto e financeiro</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Geração de caixa</span>
                </div>
                <div
                  className={`metric__value metric__value--${
                    data.result.geracaoCaixaCents >= 0 ? 'good' : 'warn'
                  }`}
                >
                  {formatCents(data.result.geracaoCaixaCents)}
                </div>
                <div className="metric__note">lucro líquido menos o investido</div>
              </div>
            </div>

            <div className="dre">
              <BlockRow
                block={block('receitaBruta')}
                label="Receita bruta"
                open={openBlocks.includes('receitaBruta')}
                onToggle={() => toggle('receitaBruta')}
                overRevenueCents={data.result.receitaLiquidaCents}
              />
              <BlockRow
                block={block('deducoes')}
                label="(−) Deduções de receita"
                open={openBlocks.includes('deducoes')}
                onToggle={() => toggle('deducoes')}
                overRevenueCents={data.result.receitaLiquidaCents}
              />
              <ResultRow
                label="= Receita líquida"
                cents={data.result.receitaLiquidaCents}
                percent={data.result.receitaLiquidaCents === 0 ? null : 100}
              />

              <BlockRow
                block={block('custosDiretos')}
                label="(−) Custos operacionais diretos"
                open={openBlocks.includes('custosDiretos')}
                onToggle={() => toggle('custosDiretos')}
                overRevenueCents={data.result.receitaLiquidaCents}
              />
              <ResultRow
                label="= Lucro bruto"
                cents={data.result.lucroBrutoCents}
                percent={data.margins.brutaPercent}
              />

              <BlockRow
                block={block('despesasOperacionais')}
                label="(−) Despesas operacionais"
                open={openBlocks.includes('despesasOperacionais')}
                onToggle={() => toggle('despesasOperacionais')}
                overRevenueCents={data.result.receitaLiquidaCents}
              />
              <ResultRow
                label="= Lucro operacional"
                cents={data.result.lucroOperacionalCents}
                percent={data.margins.operacionalPercent}
              />

              <BlockRow
                block={block('impostos')}
                label="(−) Impostos e obrigações"
                open={openBlocks.includes('impostos')}
                onToggle={() => toggle('impostos')}
                overRevenueCents={data.result.receitaLiquidaCents}
              />
              <BlockRow
                block={block('receitasFinanceiras')}
                label="(+) Receitas financeiras e aportes"
                open={openBlocks.includes('receitasFinanceiras')}
                onToggle={() => toggle('receitasFinanceiras')}
                overRevenueCents={data.result.receitaLiquidaCents}
              />
              <BlockRow
                block={block('despesasFinanceiras')}
                label="(−) Despesas financeiras"
                open={openBlocks.includes('despesasFinanceiras')}
                onToggle={() => toggle('despesasFinanceiras')}
                overRevenueCents={data.result.receitaLiquidaCents}
              />
              <ResultRow
                label="= Lucro líquido"
                cents={data.result.lucroLiquidoCents}
                percent={data.margins.liquidaPercent}
                total
              />

              <BlockRow
                block={block('investimentos')}
                label="(−) Investimentos (CAPEX)"
                open={openBlocks.includes('investimentos')}
                onToggle={() => toggle('investimentos')}
              />
              <ResultRow
                label="= Geração de caixa"
                cents={data.result.geracaoCaixaCents}
                note="investimento não é despesa do mês, mas sai do caixa"
                percent={null}
              />
            </div>

            {data.blocks.every((b) => b.accounts.length === 0) && (
              <EmptyState
                title="Nenhum lançamento neste período"
                description={
                  basis === 'competencia'
                    ? 'Os lançamentos com competência no intervalo aparecem aqui.'
                    : 'As baixas feitas no intervalo aparecem aqui. Talvez o período tenha lançamento em aberto, mas nada pago.'
                }
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

/**
 * Filtro de empresa dos relatórios. Vazio é o consolidado do grupo; escolher
 * uma empresa dá o relatório só dela.
 */
function EmpresaFiltro({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const businesses = useBusinesses();
  // Com uma empresa só, filtrar não muda nada — o seletor só atrapalharia.
  if ((businesses.data ?? []).length < 2) return null;
  return (
    <select
      className="filterbar__select"
      value={value}
      aria-label="Empresa"
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Todas as empresas</option>
      {(businesses.data ?? []).map((business) => (
        <option key={business.id} value={business.id}>
          {business.name}
        </option>
      ))}
    </select>
  );
}
